import { useRef, useState, type KeyboardEvent } from 'react';
import { AnimatePresence, LayoutGroup, motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowDownRight, ArrowUpRight, CalendarDays, MapPin, Bell } from 'lucide-react';
import './marketing/solutions.css';

const ROLES = [
  { id: 'student', label: 'For students', title: ['Less figuring out.', 'More showing up.'], copy: 'Your classes, your rooms, your rhythm. Turn a scattered week into a day that makes sense.', cta: 'Create your timetable', href: '/student-form', primary: 'Database Systems', secondary: 'Software Engineering', note: 'Your week, made personal' },
  { id: 'teacher', label: 'For teachers', title: ['Make space for', 'your best work.'], copy: 'Know where you need to be and what comes next. A clear teaching schedule, with the details already in place.', cta: 'Explore teaching tools', href: '/contact', primary: 'Database Systems', secondary: 'Programming Fundamentals', note: 'A clearer teaching day' },
  { id: 'admin', label: 'For admins', title: ['One campus.', 'A connected view.'], copy: 'Bring programs, classes, and people into one organized workspace. See how the academic day fits together.', cta: 'Open admin workspace', href: '/admin/login', primary: 'Computer Science', secondary: 'Business Administration', note: 'Your campus, in sync' },
] as const;

function ScheduleComposition({ role, reduced }: { role: (typeof ROLES)[number]; reduced: boolean }) {
  return (
    <div className="mk-planner" aria-label="Illustrative weekly timetable">
      <div className="mk-planner-caption"><span><i /> {role.note}</span><span>WEEK / 07</span></div>
      <div className="mk-planner-week" aria-hidden="true"><span /><span>MON <b>14</b></span><span className="is-today">TUE <b>15</b><i /></span><span>WED <b>16</b></span></div>
      <div className="mk-planner-grid">
        <div className="mk-planner-times" aria-hidden="true"><span>09:00</span><span>10:00</span><span>11:00</span><span>12:00</span></div>
        <div className="mk-planner-lines" aria-hidden="true"><i /><i /><i /><i /></div>
        <div className="mk-planner-event mk-planner-event--quiet" aria-hidden="true"><span>09:00 — 10:00</span><b>Design<br />principles</b><small>Room 108</small></div>
        <motion.div className="mk-planner-event mk-planner-event--main" initial={reduced ? false : { y: 18, rotate: 0, opacity: 0 }} whileInView={{ y: 0, rotate: reduced ? 0 : -4, opacity: 1 }} viewport={{ once: true }} transition={{ duration: reduced ? 0 : .75, ease: [.22, 1, .36, 1] }}>
          <div><span>UP NEXT</span><ArrowUpRight size={19} /></div>
          <strong>{role.primary}</strong>
          <p>09:00 — 10:30 AM</p>
          <span className="mk-planner-room"><MapPin size={13} /> Block C · Room 204</span>
        </motion.div>
        <div className="mk-planner-event mk-planner-event--later"><span>11:00 — 12:30</span><b>{role.secondary}</b><small>Lab 03</small></div>
        <div className="mk-planner-now" aria-hidden="true"><i /><span>YOU ARE HERE</span></div>
      </div>
      <div className="mk-planner-foot"><span>Less friction. Better flow.</span><span>Illustrative schedule ↗</span></div>
    </div>
  );
}

export function SolutionsSection() {
  const [active, setActive] = useState(0);
  const reducedMotion = !!useReducedMotion();
  const tabs = useRef<(HTMLButtonElement | null)[]>([]);
  const role = ROLES[active];
  const handleKey = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let next: number;
    if (event.key === 'ArrowRight') next = (index + 1) % ROLES.length;
    else if (event.key === 'ArrowLeft') next = (index + ROLES.length - 1) % ROLES.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = ROLES.length - 1;
    else return;
    event.preventDefault(); setActive(next); tabs.current[next]?.focus();
  };
  return (
    <section id="solutions" className="mk-solutions" aria-labelledby="mk-solutions-heading">
      <div className="mk-container">
        <div className="mk-section-index"><span>01 / THE CAMPUS, RECOMPOSED</span><span>A little order. A lot of possibility. <ArrowDownRight size={16} /></span></div>
        <div className="mk-solutions-heading">
          <motion.h2 className="mk-heading" id="mk-solutions-heading" initial={reducedMotion ? false : { opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: .8, ease: [.22, 1, .36, 1] }}>
            Goodbye, chaos.<br /><span>Hello, headspace.</span>
          </motion.h2>
          <p className="mk-copy">University is full of moving parts. <br />Your day doesn’t have to feel like it. <br />Meet your campus, beautifully connected.</p>
        </div>
        <LayoutGroup id="campus-roles">
          <div className="mk-solutions-tabs" role="tablist" aria-label="Choose your campus role">
            {ROLES.map(({ id, label }, index) => (
              <button type="button" key={id} id={`mk-role-tab-${id}`} role="tab" aria-selected={active === index} aria-controls="mk-role-panel" tabIndex={active === index ? 0 : -1} ref={element => { tabs.current[index] = element; }} onClick={() => setActive(index)} onKeyDown={event => handleKey(event, index)}>
                <span className="mk-role-number">0{index + 1}</span><span>{label}</span><ArrowUpRight size={18} />
                {active === index && <motion.i layoutId="role-underline" transition={reducedMotion ? { duration: 0 } : { type: 'spring', stiffness: 350, damping: 32 }} />}
              </button>
            ))}
          </div>
        </LayoutGroup>
        <div id="mk-role-panel" role="tabpanel" aria-labelledby={`mk-role-tab-${role.id}`} tabIndex={0}>
          <AnimatePresence mode="wait" initial={false}>
            <motion.div key={role.id} className="mk-solutions-story" initial={reducedMotion ? { opacity: 1 } : { opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: reducedMotion ? 1 : 0 }} transition={{ duration: reducedMotion ? 0 : .22 }}>
              <div className="mk-solutions-copy">
                <span className="mk-solutions-glyph" aria-hidden="true"><CalendarDays size={26} strokeWidth={1.25} /><i /><i /></span>
                <h3>{role.title[0]}<br />{role.title[1]}</h3>
                <p className="mk-copy">{role.copy}</p>
                <Link className="mk-text-link" to={role.href}>{role.cta}<ArrowUpRight size={18} /></Link>
              </div>
              <ScheduleComposition role={role} reduced={reducedMotion} />
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="mk-solutions-details">
          {[
            { Icon: CalendarDays, title: 'A rhythm of your own.', copy: 'A personal timetable. Every class in its place.' },
            { Icon: MapPin, title: 'From here to there.', copy: 'Room details and campus routes, connected.' },
            { Icon: Bell, title: 'One step ahead.', copy: 'Classes, exams, and the dates that matter.' },
          ].map(({ Icon, title, copy }, index) => (
            <motion.div key={title} initial={reducedMotion ? false : { opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: .6, delay: index * .1 }}>
              <span><Icon size={20} strokeWidth={1.3} /><small>0{index + 1}</small></span><h4>{title}</h4><p>{copy}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
export default SolutionsSection;
